/**
 * Voice-line definitions (R12) — the §5 narrative layer's speakable lines,
 * with the full policy surface (ruled by Mike, 2026-08-22: pools, cooldowns,
 * and one-shot behavior land together).
 *
 * @remarks
 * A voice line is a SPEAKABLE — a named line (or ordered pool of lines) that
 * anything with effects can utter via `speak_voice_line`: an interaction, an
 * event, a trigger. What the definition owns is the POLICY of its own
 * speaking; the WHEN stays outside, exactly as events ruled in R9.
 *
 * - **One-shot attaches to the LINE, not the speaker** (`once`): several
 *   watchers may compete to speak the same line and only the first wins —
 *   this is what `voiceLineUsage` records that `firedTriggerIds` cannot.
 * - **`pool` is a D30 sequential pool**: the nth speaking picks the nth
 *   entry and HOLDS on the last — the joke lands once, the workhorse line
 *   carries the rest of the session.
 * - **Each entry's `text` is D55 conditional text**, which is how a register
 *   switch is data rather than machinery: one line, two readings, switched
 *   by a flag (the blind-EURYDICE register is the named consumer).
 * - **`cooldownTurns`** is the minimum number of EXECUTED turns between
 *   speakings — a re-speakable line that refuses to become a metronome.
 * - **`conditions`** gate every speaking; an unmet speak is a silent skip,
 *   never an error (the scheduler's drop semantics, voice-side).
 *
 * A skipped speak (once-spent, cooling down, conditions unmet) consumes
 * nothing: no pool advance, no cooldown reset, no transcript entry.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionalTextSchema } from './conditional-text.js';
import { conditionSchema } from './conditions.js';
import { voiceLineIdSchema } from './identifiers.js';
import { nonNegativeIntegerSchema } from './primitives.js';
import { transcriptPrioritySchema, transcriptSourceSchema } from './transcript.js';

/**
 * One pool entry: a narrative line whose text may vary with world state.
 *
 * @remarks
 * The same field trio as {@link narrativeLineSchema}, with `text` widened to
 * conditional text — a plain string stays a plain string, unmigrated.
 */
export const voiceLinePoolEntrySchema = z
	.strictObject({
		priority: transcriptPrioritySchema.default('standard'),
		source: transcriptSourceSchema,
		text: conditionalTextSchema,
	})
	.readonly();

/** One pool entry — see {@link voiceLinePoolEntrySchema}. */
export type VoiceLinePoolEntry = z.infer<typeof voiceLinePoolEntrySchema>;

/** One authored voice line — see the module remarks. */
export const voiceLineDefinitionSchema = z
	.strictObject({
		/** Gate every speaking; unmet means a silent skip (default: always speakable). */
		conditions: z.array(conditionSchema).readonly().default([]),
		/** Minimum executed turns between speakings; absent means no cooldown. */
		cooldownTurns: nonNegativeIntegerSchema.optional(),
		/** Fires at most once per session when true (default: re-speakable). */
		once: z.boolean().default(false),
		/** The pool — at least one entry; read in the order `poolOrder` names. */
		pool: z.array(voiceLinePoolEntrySchema).min(1).readonly(),
		/**
		 * D141: how the pool is read. `sequential` (D30, the default) — the
		 * nth speaking picks the nth entry and holds on the last: a line that
		 * escalates and then settles. `shuffled` — every entry once per cycle in
		 * a seeded permutation, a fresh permutation each cycle whose first entry
		 * never repeats the last one heard: the drips and the rats, where the
		 * player must not be able to predict the order and must not hear the
		 * same line twice before the pool has turned over. Replay-safe by
		 * construction: the permutation is derived from the session seed, the
		 * line's id, and the cycle number, so it survives a save without being
		 * saved (D139's contract, met without new state).
		 */
		poolOrder: z.enum(['sequential', 'shuffled']).default('sequential'),
		voiceLineId: voiceLineIdSchema,
	})
	.readonly()
	.refine((voiceLine) => voiceLine.poolOrder === 'sequential' || voiceLine.pool.length > 1, {
		message: 'a shuffled pool needs at least two entries — one entry has one order, and the declaration would be dead content',
		path: ['poolOrder'],
	});

/** One authored voice line — see {@link voiceLineDefinitionSchema}. */
export type VoiceLineDefinition = z.infer<typeof voiceLineDefinitionSchema>;
